import { Alert, Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
  Navigate,
} from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { GlobalMailOverview } from '../Admin/GlobalMail/GlobalMailOverview';
import { License } from '../Admin/License';
import { RatingFormOverview } from '../Admin/RatingForm/RatingFormOverview';
import { SysRegOverview } from '../Admin/SysReg/SysRegOverview.';
import { AuditSearchOverview } from '../Audit/AuditSearchOverview';
import { ArnoTopAdminView } from '../Admin/ArnoTop/ArnoTopAdminView';
import { DecisionTreeEditor } from '../Admin/ArnoTop/DecisionTreeEditor';
import { WordPluginGate } from '../Admin/ArnoTop/WordPluginGate';
import { WordPluginAccessOverview } from '../Admin/Dev/WordPluginAccessOverview';
import { HealthCheckAccessOverview } from '../Admin/Dev/HealthCheckAccessOverview';
import { GwGPruefungAccessOverview } from '../Admin/Dev/GwGPruefungAccessOverview';
import { FinTSInstituteOverview } from '../Admin/FinTSInstitute/FinTSInstituteOverview';
import { ProcessManagerAdminOverview } from '../Admin/ProcessManager/ProcessManagerAdminOverview';
import { DevHubOverview } from '../Admin/Dev/DevHubOverview';
import { KiSettingsOverview } from '../Admin/Ki/KiSettingsOverview';
import { SeedImportOverview } from '../Admin/Dev/SeedImportOverview';
import { CalendarMain } from '../Calendar/CalendarMain';
import { RoomOccupancyPlan } from '../RealEstate/RoomOccupancyPlan';
import { Authorize } from '../Authorize/Authorize';
import { Changelog } from '../Changelog/Changelog';
import { ClientsOverview } from '../Clients/ClientsOverview';
import { GrundstueckeOverview } from '../Grundstuecke/GrundstueckeOverview';
import { GrundbuchblattEditRoute } from '../Grundstuecke/GrundbuchblattEditRoute';
import { CreateLegalClientDialog } from '../Clients/CreateLegalClientDialog';
import { EditLegalClientDialog } from '../Clients/EditLegalClientDialog';
import { LegalClientGroupOverview } from '../LegalClientGroup/LegalClientGroupOverview';
import { DashboardMain } from '../Dashboard/DashboardMain';
import { DayOffWorkOverview } from '../DayOffWork/DayOffWorkOverview';
import { DocumentPortalOverview } from '../DocumentPortal/DocumentPortalOverview';
import { useFetch } from '../hooks/useFetch';
import { IAcademicTitle } from '../Interfaces/IAcademicTitle';
import { IAddressType } from '../Interfaces/IAddressType';
import { IFederalState } from '../Interfaces/IAemter';
import { IBusinessForm } from '../Interfaces/IBusinessForm';
import { ICompanyFormationOption } from '../Interfaces/ICompanyFormationOption';
import { ICompanyFormationQuestion } from '../Interfaces/ICompanyFormationQuestion';
import { IConfig } from '../Interfaces/IConfig';
import { IContactGroup } from '../Interfaces/IContactGroup';
import { ICountry } from '../Interfaces/ICountry';
import { IDictionaryEntry } from '../Interfaces/IDictionaryEntry';
import { IGender } from '../Interfaces/IGender';
import { ILandMarking } from '../Interfaces/ILandMarking';
import { ILegalRequirementState } from '../Interfaces/ILegalRequirementState';
import { IMIMEType } from '../Interfaces/IMIMEType';
import { IRelationshipRole } from '../Interfaces/IRelationshipRole';
import { IRelationshipType } from '../Interfaces/IRelationshipType';
import { IRelationshipTypeHasRelationshipRole } from '../Interfaces/IRelationshipTypeHasRelationshipRole';
import { ITextModule } from '../Interfaces/ITextModule';
import { ITextModuleCategory } from '../Interfaces/ITextModuleCategory';
import { DialogInboxOverview } from '../DialogInbox/DialogInboxOverview';
import { KanbanOverview } from '../Kanban/KanbanOverview';
import { LegalTransactionOverview } from '../LegalTransaction/LegalTransactionOverview';
import { LegalTransactionTypeOverview } from '../LegalTransactionType/LegalTransactionTypeOverview';
import {
  actionCreatorsAcademicTitle,
  actionCreatorsAddressType,
  actionCreatorsBusinessForm,
  actionCreatorsCompanyFormationOption,
  actionCreatorsCompanyFormationQuestion,
  actionCreatorsContactGroup,
  actionCreatorsCountry,
  actionCreatorsDictionaryEntry,
  actionCreatorsDistrictCourt,
  actionCreatorsFederalState,
  actionCreatorsGender,
  actionCreatorsLandMarking,
  actionCreatorsLegalRequirementState,
  actionCreatorsMIMType,
  actionCreatorsRelationshipRole,
  actionCreatorsRelationshipType,
  actionCreatorsRelationshipTypeHasRole,
  actionCreatorsTextModule,
  actionCreatorsTextModuleCategory,
} from '../redux/actionCreators';
import { SystemMailOverview } from '../SystemMail/SystemMailOverview';
import { TextModulSectionMain } from '../TextModule/TextModulSectionMain';
import { TimeRecordOverview } from '../TimeRecord/Management/TimeRecordOverview';
import { ToolsOverview } from '../Tools/ToolsOverview';
import XML_Konverter from '../Tools/XML_Konverter';
import { AppShell } from './AppShell';
import { LoadingSpinner } from './LoadingSpinner';
import { InvoiceOverview } from '../Invoice/InvoiceOverview';
import { IDistrictCourt } from '../Interfaces/IDistrictCourt';
import { SystemSettingsView } from '../Admin/SystemSettings/SystemSettingsView';
import { ProcessManagerTrackingOverview } from '../ProcessManager/ProcessManagerTrackingOverview';
import { TrackingSectionMain } from '../ProcessManager/TrackingSectionMain';
import { InvoicesView } from '../Invoice/InvoicesView';
import { TaskOverview } from '../Task/TaskOverview';
import { AccountView } from '../Account/AccountView';
import { ReportsHome } from '../Reports/ReportsHome';

interface IProps {
  configObject: IConfig;
}

export const PageRouter: React.FC<IProps> = ({ configObject }) => {
  const dispatch = useDispatch();
  // Contact
  const { setGenderArray } = bindActionCreators(actionCreatorsGender, dispatch);
  const { setAcademicTitleArray } = bindActionCreators(
    actionCreatorsAcademicTitle,
    dispatch
  );
  const { setCountryArray } = bindActionCreators(
    actionCreatorsCountry,
    dispatch
  );
  const { setBusinessFormArray } = bindActionCreators(
    actionCreatorsBusinessForm,
    dispatch
  );
  const { setContactGroupArray } = bindActionCreators(
    actionCreatorsContactGroup,
    dispatch
  );
  // LegalClient
  const { setRelationshipTypeArray } = bindActionCreators(
    actionCreatorsRelationshipType,
    dispatch
  );
  const { setRelationshipRoleArray } = bindActionCreators(
    actionCreatorsRelationshipRole,
    dispatch
  );
  const { setRelationshipTypeHasRoleArray } = bindActionCreators(
    actionCreatorsRelationshipTypeHasRole,
    dispatch
  );
  const { setAdressTypeArray } = bindActionCreators(
    actionCreatorsAddressType,
    dispatch
  );
  // Dokumente
  const { setMIMETypeArray } = bindActionCreators(
    actionCreatorsMIMType,
    dispatch
  );
  // Legal Transaction
  const { setLegalRequirementState } = bindActionCreators(
    actionCreatorsLegalRequirementState,
    dispatch
  );
  // CompanyFormation
  const { setCompanyFormationQuestionArray } = bindActionCreators(
    actionCreatorsCompanyFormationQuestion,
    dispatch
  );
  const { setCompanyFormationOptionArray } = bindActionCreators(
    actionCreatorsCompanyFormationOption,
    dispatch
  );
  // Text Module
  const { setTextModuleCategoryArray } = bindActionCreators(
    actionCreatorsTextModuleCategory,
    dispatch
  );
  const { setTextModuleArray } = bindActionCreators(
    actionCreatorsTextModule,
    dispatch
  );
  // Variable
  const { setDictionaryEntry } = bindActionCreators(
    actionCreatorsDictionaryEntry,
    dispatch
  );
  // Amtsgerichte
  const { setDistrictCourtArray } = bindActionCreators(
    actionCreatorsDistrictCourt,
    dispatch
  );
  const { setLandMarkingArray } = bindActionCreators(
    actionCreatorsLandMarking,
    dispatch
  );

  const { setFederalStateArray } = bindActionCreators(
    actionCreatorsFederalState,
    dispatch
  );

  //
  const [scopeArray, setCcopeArray] = React.useState(
    localStorage.getItem('scope')?.split(',')
  );
  // Laden aus dem Backend
  // Contact
  const [
    localGenderArray,
    setLocalGenderArray,
    wasLocalSuccessfullyGenderArray,
  ] = useFetch<IGender[]>('/gender');
  const [
    localAcademicTitleArray,
    setLocalAcademicTitleArray,
    wasLocalSuccessfullyAcademicTitleArray,
  ] = useFetch<IAcademicTitle[]>('/academictitle');
  const [
    localCountryArray,
    setlocalCountryArray,
    wasLocalSuccessfullyCountryArray,
  ] = useFetch<ICountry[]>('/country');
  const [
    localBusinessFormArray,
    setlocalBusinessFormArray,
    wasLocalSuccessfullyBusinessFormArray,
  ] = useFetch<IBusinessForm[]>('/businessform');
  const [
    localContactGroupArray,
    setlocalContactGroupArray,
    wasLocalSuccessfullyContactGroupArray,
  ] = useFetch<IContactGroup[]>('/contactgroup');
  // LegalClient
  const [
    localRelationshipType,
    setLocalRelationshipType,
    wasLocalSuccessfullyLocalRelationshipType,
  ] = useFetch<IRelationshipType[]>('/relationshiptype');
  const [
    localRelationshipRole,
    setLocalRelationshipRole,
    wasLocalSuccessfullyLocalRelationshipRole,
  ] = useFetch<IRelationshipRole[]>('/relationshiprole');
  const [
    localRelationshipTypeHasRole,
    setLocalRelationshipTypeHasRole,
    wasLocalSuccessfullyLocalRelationshipTypeHasRole,
  ] = useFetch<IRelationshipTypeHasRelationshipRole[]>(
    '/relationshiptype_has_relationshiprole'
  );
  const [
    localAddressTypeArray,
    setLocalAddressTypeArray,
    wasLocalSuccessfullyAddressTypeArray,
  ] = useFetch<IAddressType[]>('/addresstype');
  // Dokumente
  const [
    localMIMETypeArray,
    setlocalMIMETypeArray,
    wasLocalSuccessfullyMIMETypeArray,
  ] = useFetch<IMIMEType[]>('/mimetype');
  // Legal Transactions
  const [
    localLegalRequirementStateArray,
    setlocalLegalRequirementStateArray,
    wasLocalSuccessfullyLegalRequirementStateArray,
  ] = useFetch<ILegalRequirementState[]>('/legalrequirementstate');
  // CompanyFormation
  const [
    localCompanyFormationQuestionArray,
    setlocalCompanyFormationQuestionArray,
    wasLocalSuccessfullyCompanyFormationQuestionArray,
  ] = useFetch<ICompanyFormationQuestion[]>('/companyformationquestion');
  const [
    localCompanyFormationOptionArray,
    setlocalCompanyFormationOptionArray,
    wasLocalSuccessfullyCompanyFormationOptionArray,
  ] = useFetch<ICompanyFormationOption[]>('/companyformationoption');
  // Text Module
  const [
    localTextModuleCategoryArray,
    setLocalTextModuleCategoryArray,
    wasLocalSuccessfullyTextModuleCategoryArray,
  ] = useFetch<ITextModuleCategory[]>('/textmodulecategory');
  const [
    localTextModuleArray,
    setLocalTextModuleArray,
    wasLocalSuccessfullyTextModuleArray,
  ] = useFetch<ITextModule[]>('/textmodule');
  // Variable
  const [
    localDictionaryEntryArray,
    setLocalDictionaryEntryArray,
    wasLocalSuccessfullyLocalDictionaryEntryArray,
  ] = useFetch<IDictionaryEntry[]>('/dictionaryentry');
  // Amtsgerichte
  const [
    localDistrictCourtArray,
    setLocalDistrictCourtArray,
    wasSuccessullyLocalDistrictCourtArray,
  ] = useFetch<IDistrictCourt[]>('/districtcourt');
  const [
    localLandMarkingArray,
    setLocalLandMarkingArray,
    wasSuccessfullyLocalLandMarkingArray,
  ] = useFetch<ILandMarking[]>('/landmarking');
  const [
    localFederalStateArray,
    setlocalFederalStateArray,
    wasSuccessfullylocalFederalStateArray,
  ] = useFetch<IFederalState[]>('/federalstate');

  // Speichern in Redux
  // Contact
  useEffect(() => {
    if (localGenderArray) {
      setGenderArray(localGenderArray);
    }
  }, [localGenderArray]);
  useEffect(() => {
    if (localAcademicTitleArray) {
      setAcademicTitleArray(localAcademicTitleArray);
    }
  }, [localAcademicTitleArray]);
  useEffect(() => {
    if (localCountryArray) {
      setCountryArray(localCountryArray);
    }
  }, [localCountryArray]);
  useEffect(() => {
    if (localBusinessFormArray) {
      setBusinessFormArray(localBusinessFormArray);
    }
  }, [localBusinessFormArray]);
  useEffect(() => {
    if (localContactGroupArray) {
      setContactGroupArray(localContactGroupArray);
    }
  }, [localContactGroupArray]);
  // LegalClient
  useEffect(() => {
    if (localRelationshipType) {
      setRelationshipTypeArray(localRelationshipType);
    }
  }, [localRelationshipType]);
  useEffect(() => {
    if (localRelationshipRole) {
      setRelationshipRoleArray(localRelationshipRole);
    }
  }, [localRelationshipRole]);
  useEffect(() => {
    if (localRelationshipTypeHasRole) {
      setRelationshipTypeHasRoleArray(localRelationshipTypeHasRole);
    }
  }, [localRelationshipTypeHasRole]);
  useEffect(() => {
    if (localAddressTypeArray) {
      setAdressTypeArray(localAddressTypeArray);
    }
  }, [localAddressTypeArray]);
  // Dokumente
  useEffect(() => {
    if (localMIMETypeArray) {
      setMIMETypeArray(localMIMETypeArray);
    }
  }, [localMIMETypeArray]);
  // Legal Transaction
  useEffect(() => {
    if (localLegalRequirementStateArray) {
      setLegalRequirementState(localLegalRequirementStateArray);
    }
  }, [localLegalRequirementStateArray]);
  useEffect(() => {
    if (localLegalRequirementStateArray) {
      setLegalRequirementState(localLegalRequirementStateArray);
    }
  }, [localLegalRequirementStateArray]);
  // CompanyFormation
  useEffect(() => {
    if (localCompanyFormationQuestionArray) {
      setCompanyFormationQuestionArray(localCompanyFormationQuestionArray);
    }
  }, [localCompanyFormationQuestionArray]);
  useEffect(() => {
    if (localCompanyFormationOptionArray) {
      setCompanyFormationOptionArray(localCompanyFormationOptionArray);
    }
  }, [localCompanyFormationOptionArray]);
  // Text Module
  useEffect(() => {
    if (localTextModuleCategoryArray) {
      setTextModuleCategoryArray(localTextModuleCategoryArray);
    }
  }, [localTextModuleCategoryArray]);
  useEffect(() => {
    if (localTextModuleArray) {
      setTextModuleArray(localTextModuleArray);
    }
  }, [localTextModuleArray]);
  // Variable
  useEffect(() => {
    if (localDictionaryEntryArray) {
      setDictionaryEntry(localDictionaryEntryArray);
    }
  }, [localDictionaryEntryArray]);
  // Amtsgerichte
  useEffect(() => {
    if (localDistrictCourtArray) {
      setDistrictCourtArray(localDistrictCourtArray);
    }
  }, [localDistrictCourtArray]);
  useEffect(() => {
    if (localLandMarkingArray) {
      setLandMarkingArray(localLandMarkingArray);
    }
  }, [localLandMarkingArray]);
  useEffect(() => {
    if (localFederalStateArray) {
      setFederalStateArray(localFederalStateArray);
    }
  }, [localFederalStateArray]);

  const checkPermission = (permission: string, componente: React.ReactNode) => {
    if (scopeArray !== undefined && scopeArray.indexOf(permission) > -1) {
      return componente;
    } else {
      return <Alert severity='error'>Fehlende Berechtigung!</Alert>;
    }
  };

  if (
    localStorage === undefined ||
    !wasLocalSuccessfullyGenderArray ||
    !wasLocalSuccessfullyAcademicTitleArray ||
    !wasLocalSuccessfullyCountryArray ||
    !wasLocalSuccessfullyContactGroupArray ||
    // LegalClient
    !wasLocalSuccessfullyLocalRelationshipType ||
    !wasLocalSuccessfullyLocalRelationshipRole ||
    !wasLocalSuccessfullyLocalRelationshipTypeHasRole ||
    !wasLocalSuccessfullyAddressTypeArray ||
    // Documente
    !wasLocalSuccessfullyMIMETypeArray ||
    // CompanyFormation
    !wasLocalSuccessfullyCompanyFormationQuestionArray ||
    !wasLocalSuccessfullyCompanyFormationOptionArray ||
    //
    !wasLocalSuccessfullyLocalDictionaryEntryArray ||
    // Text Module
    !wasLocalSuccessfullyTextModuleCategoryArray ||
    !wasLocalSuccessfullyTextModuleArray
  ) {
    return (
      <Alert severity='error'>Es ist ein fataler Fehler aufgertetten!</Alert>
    );
  } else if (
    false
    //localGenderArray === undefined ||
    //localAcademicTitleArray === undefined ||
    //localCountryArray === undefined ||
    //localContactGroupArray === undefined ||
    // LegalClient
    //localRelationshipType === undefined ||
    //localRelationshipRole === undefined ||
    //localRelationshipTypeHasRole === undefined ||
    //localAddressTypeArray === undefined ||
    // Dokuemten
    //localMIMETypeArray === undefined ||
    // CompanyFormation
    //localCompanyFormationQuestionArray === undefined ||
    //localCompanyFormationOptionArray === undefined ||
    // Text Module
    //localTextModuleCategoryArray === undefined ||
    //localTextModuleArray === undefined ||
    // Variable
    //localDictionaryEntryArray === undefined ||
    // Amtsgericht
    //localDistrictCourtArray === undefined ||
    //localLandMarkingArray === undefined ||
    //localFederalStateArray === undefined
  ) {
    return <LoadingSpinner height='100%' />;
  } else {
    return (
      <>
        <Router>
          <AppShell configObject={configObject}>
            <Routes>
              <Route path='/' element={<DashboardMain />} />
              <Route
                path='/dialoginbox'
                element={checkPermission(
                  'legaltransaction',
                  <DialogInboxOverview />
                )}
              />
              <Route path='/kanban' element={<KanbanOverview />} />
              <Route
                path='/task'
                element={checkPermission('all', <TaskOverview />)}
              />
              <Route
                path='/task/:idTask'
                element={checkPermission('all', <TaskOverview />)}
              />
              <Route
                path='/order_book'
                element={checkPermission(
                  'legaltransaction',
                  <LegalTransactionOverview
                    key={`legaltransaction-order-book`}
                    title='Auftragsbuch'
                    description='Im Auftragsbuch werden alle neu angelegten Vorgänge angezeigt, unabhängig davon, ob diese über Dialoge erstellt oder manuell angelegt wurden. Die Vorgänge verbleiben im Auftragsbuch bis zur Beurkundung. Ab der Phase „Abwicklung“ werden sie unter „Vorgänge“ geführt.'
                    url='order_book'
                    enableAdd
                    showClosed={false}
                    maxIdLegalTransactionProgress={20}
                  />
                )}
              />
              <Route
                path='/order_book/:idLegalTransaction'
                element={checkPermission(
                  'legaltransaction',
                  <LegalTransactionOverview
                    key={`legaltransaction-order-book`}
                    title='Auftragsbuch'
                    description='Im Auftragsbuch werden alle neu angelegten Vorgänge angezeigt, unabhängig davon, ob diese über Dialoge erstellt oder manuell angelegt wurden. Die Vorgänge verbleiben im Auftragsbuch bis zur Beurkundung. Ab der Phase „Abwicklung“ werden sie unter „Vorgänge“ geführt.'
                    url='order_book'
                    enableAdd
                    maxIdLegalTransactionProgress={20}
                  />
                )}
              />
              <Route
                path='/legaltransaction'
                element={checkPermission(
                  'legaltransaction',
                  <LegalTransactionOverview
                    key={`legaltransaction-legaltransaction`}
                    title='Vorgänge'
                    description='Unter Vorgänge werden alle Vorgänge angezeigt, die mindestens die Phase „Abwicklung“ erreicht haben.'
                    url='legaltransaction'
                    enableAdd={false}
                    showClosed={false}
                    showRecordNumberColumn={true}
                    minIdLegalTransactionProgress={30}
                  />
                )}
              />
              <Route
                path='/legaltransaction/:idLegalTransaction'
                element={checkPermission(
                  'legaltransaction',
                  <LegalTransactionOverview
                    key={`legaltransaction-legaltransaction`}
                    title='Vorgänge'
                    description='Unter Vorgänge werden alle Vorgänge angezeigt, die mindestens die Phase „Abwicklung“ erreicht haben.'
                    url='legaltransaction'
                    enableAdd={false}
                    showRecordNumberColumn={true}
                    minIdLegalTransactionProgress={30}
                  />
                )}
              />
              <Route
                path='/archive'
                element={checkPermission(
                  'legaltransaction',
                  <LegalTransactionOverview
                    key={`legaltransaction-archive`}
                    title='Archiv'
                    description='Alle geschlossenen Vorgänge – ob durch Stornierung oder regulären Abschluss beendet wurden – befinden sich im Archiv. Hier ist eine nachträgliche Einsicht in die Vorgänge möglich. Administratoren können archivierte Vorgänge reaktivieren.'
                    url='archive'
                    enableAdd={false}
                    showClosed={true}
                    showRecordNumberColumn={true}
                  />
                )}
              />
              <Route
                path='/archive/:idLegalTransaction'
                element={checkPermission(
                  'legaltransaction',
                  <LegalTransactionOverview
                    key={`legaltransaction-archive`}
                    title='Archiv'
                    description='Alle geschlossenen Vorgänge – ob durch Stornierung oder regulären Abschluss beendet wurden – befinden sich im Archiv. Hier ist eine nachträgliche Einsicht in die Vorgänge möglich. Administratoren können archivierte Vorgänge reaktivieren.'
                    url='archive'
                    enableAdd={false}
                    showClosed={true}
                    showRecordNumberColumn={true}
                  />
                )}
              />
              {/*
              <Route
                path='/invoices'
                element={checkPermission('bill', <InvoiceOverview />)}
              />
              <Route
                path='/invoices/:idInvoice'
                element={checkPermission('bill', <InvoiceOverview />)}
              />

              */}
              // Redirect von /invoices auf die neue View
              <Route
                path='/invoices'
                element={checkPermission(
                  'bill',
                  <Navigate to='/invoices/view/overview' replace />
                )}
              />
              {/* Container-View mit interner Umschaltung */}
              <Route
                path='/invoices/view/:currentView'
                element={checkPermission('bill', <InvoicesView />)}
              />
              {/* Detailansicht darf bleiben wie bisher */}
              <Route
                path='/invoices/:idInvoice'
                element={checkPermission('bill', <InvoiceOverview />)}
              />
              <Route
                path='/legaltransactiontypes'
                element={checkPermission(
                  'legaltransaction',
                  <LegalTransactionTypeOverview />
                )}
              />
              <Route
                path='/textmodules'
                element={checkPermission(
                  'legaltransaction',
                  <Navigate to='/textmodules/view/categories' replace />
                )}
              />
              <Route
                path='/textmodules/view/:currentView'
                element={checkPermission(
                  'legaltransaction',
                  <TextModulSectionMain />
                )}
              />
              <Route
                path='/legalclientgroups'
                element={checkPermission(
                  'legaltransaction',
                  <LegalClientGroupOverview />
                )}
              />
              <Route
                path='/calendar'
                element={checkPermission('all', <CalendarMain />)}
              />
              <Route
                path='/calendar/belegungsplan'
                element={checkPermission(
                  'all',
                  <Box sx={{ p: 2 }}>
                    <Typography variant='h5' sx={{ mb: 2 }}>
                      Belegungsplan
                    </Typography>
                    <RoomOccupancyPlan />
                  </Box>
                )}
              />
              <Route
                path='/authorize'
                element={checkPermission('all', <Authorize />)}
              />
              <Route
                path='/clients'
                element={checkPermission(
                  'legaltransaction',
                  <>
                    <ClientsOverview />
                    <Outlet />
                  </>
                )}
              >
                <Route
                  path=':legalClientId'
                  element={checkPermission(
                    'legaltransaction',
                    <EditLegalClientDialog />
                  )}
                />
                <Route
                  path='new'
                  element={checkPermission(
                    'legaltransaction',
                    <CreateLegalClientDialog />
                  )}
                />
              </Route>
              <Route
                path='/grundstuecke'
                element={checkPermission(
                  'legaltransaction',
                  <>
                    <GrundstueckeOverview />
                    <Outlet />
                  </>
                )}
              >
                <Route
                  path=':idGrundbuchblatt'
                  element={checkPermission(
                    'legaltransaction',
                    <GrundbuchblattEditRoute />
                  )}
                />
              </Route>
              <Route
                path='/document_portal'
                element={checkPermission(
                  'legaltransaction',
                  <DocumentPortalOverview />
                )}
              />
              <Route
                path='/process_manager_tracking'
                element={checkPermission(
                  'legaltransaction',
                  <ProcessManagerTrackingOverview />
                )}
              />
              <Route
                path='/tracking/:currentView'
                element={checkPermission(
                  'legaltransaction',
                  <TrackingSectionMain />
                )}
              />
              {/* 
              <Route
                path='/users'
                element={checkPermission('user', <UserOverview />)}
              />
              <Route
                path='/usergroups'
                element={checkPermission('user', <UserGroupOverview />)}
              />
              */}
              <Route
                path='/daysoffwork'
                element={checkPermission('user', <DayOffWorkOverview />)}
              />
              <Route
                path='/timerecording'
                element={checkPermission(
                  'timerecording',
                  <TimeRecordOverview />
                )}
              />
              <Route
                path='/system_mail/:currentView'
                element={checkPermission(
                  'systemmails',
                  <SystemMailOverview isAdmin={false} />
                )}
              />
              <Route
                path='/system_mail/:currentView/:idSystemMail'
                element={checkPermission(
                  'systemmails',
                  <SystemMailOverview isAdmin={false} />
                )}
              />
              <Route
                path='/xmlKonverter'
                element={checkPermission('legaltransaction', <XML_Konverter />)}
              />
              <Route
                path='/tools'
                element={checkPermission('legaltransaction', <ToolsOverview />)}
              />
              {/*
              <Route
                path='/admin/fsdirectory'
                element={checkPermission('admin', <FSDirectoryOverview />)}
              />
  

              <Route
                path='/admin/language'
                element={checkPermission('admin', <LanguageSectionOverview />)}
              />
              <Route
                path='/admin/public'
                element={checkPermission('admin', <PublicServer />)}
              />
            */}
              <Route
                path='/admin/globalmail'
                element={<Navigate to='/admin/globalmail/list' />}
              />
              <Route
                path='/admin/globalmail/:view'
                element={checkPermission('admin', <GlobalMailOverview />)}
              />
              <Route
                path='/admin/sysreg'
                element={checkPermission('admin', <SysRegOverview />)}
              />
              <Route
                path='/admin/audit'
                element={checkPermission('admin', <AuditSearchOverview />)}
              />
              <Route
                path='/admin/dev'
                element={checkPermission('dev', <DevHubOverview />)}
              />
              <Route
                path='/admin/fints-institutes'
                element={checkPermission('admin', <FinTSInstituteOverview />)}
              />
              <Route
                path='/admin/dev/processmanager'
                element={checkPermission(
                  'dev',
                  <ProcessManagerAdminOverview />
                )}
              />
              <Route
                path='/admin/dev/seed'
                element={checkPermission('dev', <SeedImportOverview />)}
              />
              <Route
                path='/admin/dev/ki'
                element={checkPermission('dev', <KiSettingsOverview />)}
              />
              <Route
                path='/admin/dev/word-plugin'
                element={checkPermission('dev', <WordPluginAccessOverview />)}
              />
              <Route
                path='/admin/dev/healthcheck'
                element={checkPermission('dev', <HealthCheckAccessOverview />)}
              />
              <Route
                path='/admin/dev/gwgpruefung'
                element={checkPermission('dev', <GwGPruefungAccessOverview />)}
              />
              <Route
                path='/admin/system_mail/:currentView'
                element={checkPermission(
                  'systemmails',
                  <SystemMailOverview isAdmin={true} />
                )}
              />
              <Route
                path='/admin/system_mail/:currentView/:idSystemMail'
                element={checkPermission(
                  'systemmails',
                  <SystemMailOverview isAdmin={true} />
                )}
              />
              <Route
                path='/rating'
                element={checkPermission('systemmails', <RatingFormOverview />)}
              />
              {/* Vor dem Sammelpfad /admin/:currentView, sonst faengt der die Adresse ab. */}
              <Route
                path='/admin/arnotop'
                element={<Navigate to='/admin/arnotop/entscheidungsbaeume' />}
              />
              {/* Vor dem Reiterpfad, sonst wuerde er den Editor als Reiter lesen. */}
              <Route
                path='/admin/arnotop/editor/:idDecisionTemplateVersion'
                element={checkPermission(
                  'admin',
                  <WordPluginGate>
                    <DecisionTreeEditor />
                  </WordPluginGate>
                )}
              />
              <Route
                path='/admin/arnotop/:currentView'
                element={checkPermission(
                  'admin',
                  <WordPluginGate>
                    <ArnoTopAdminView />
                  </WordPluginGate>
                )}
              />
              <Route
                path='/admin/:currentView'
                element={checkPermission('admin', <SystemSettingsView />)}
              />
              <Route
                path='/systeminfo'
                element={checkPermission('all', <License />)}
              />
              <Route
                path='/changelog'
                element={checkPermission('all', <Changelog />)}
              />
              <Route
                path='/account'
                element={checkPermission('all', <AccountView />)}
              />
              <Route
                path='/reports'
                element={checkPermission('reports', <ReportsHome />)}
              />
            </Routes>
          </AppShell>
        </Router>
      </>
    );
  }
};
